import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Card, CardHeader, Button, Input, Select, Textarea, Spinner } from '../components/ui';
import { useMedicationStore } from '../stores';
import { medicationSchema, type MedicationFormData } from '../utils/validation';

const formOptions = [
  { value: 'injection', label: 'Injection' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'liquid', label: 'Liquid' },
  { value: 'cream', label: 'Cream' },
  { value: 'patch', label: 'Patch' },
];

export const MedicationForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    createMedication,
    updateMedication,
    getMedicationById,
    uploadPhoto,
    selectedMedication,
    isLoading,
  } = useMedicationStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MedicationFormData>({
    resolver: zodResolver(medicationSchema),
    defaultValues: {
      name: '',
      description: '',
      dosage: '',
      form: '',
      price: 0,
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      getMedicationById(id);
    }
  }, [getMedicationById, id, isEdit]);

  useEffect(() => {
    if (isEdit && selectedMedication) {
      reset({
        name: selectedMedication.name,
        description: selectedMedication.description,
        dosage: selectedMedication.dosage,
        form: selectedMedication.form,
        price: selectedMedication.price,
      });
      if (selectedMedication.photo) {
        setPhotoPreview(selectedMedication.photo);
      }
    }
  }, [isEdit, selectedMedication, reset]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: MedicationFormData) => {
    setIsSubmitting(true);
    try {
      if (isEdit && id) {
        await updateMedication(id, data);
        if (photoFile) {
          await uploadPhoto(id, photoFile);
        }
        toast.success('Medication updated successfully');
      } else {
        const newMed = await createMedication(data);
        if (photoFile) {
          await uploadPhoto(newMed.id, photoFile);
        }
        toast.success('Medication created successfully');
      }
      navigate('/medications');
    } catch (error) {
      toast.error(isEdit ? 'Failed to update medication' : 'Failed to create medication');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/medications')}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-secondary-500 dark:text-secondary-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
            {isEdit ? 'Edit Medication' : 'Add Medication'}
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            {isEdit ? 'Update medication details' : 'Add a new medication to your catalog'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader title="Medication Details" />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              Product Photo
            </label>
            <div className="flex items-start space-x-4">
              <div className="w-32 h-32 rounded-xl bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center overflow-hidden border-2 border-dashed border-secondary-300 dark:border-secondary-600">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <PhotoIcon className="h-10 w-10 text-secondary-400" />
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Photo
                </Button>
                {photoPreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleRemovePhoto}
                    className="ml-2"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </Button>
                )}
                <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
                  PNG, JPG up to 5MB. Recommended size: 400x400px
                </p>
              </div>
            </div>
          </div>

          <Input
            label="Medication Name"
            placeholder="e.g., Semaglutide"
            error={errors.name?.message}
            {...register('name')}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Dosage"
              placeholder="e.g., 0.5mg"
              error={errors.dosage?.message}
              {...register('dosage')}
            />
            <Select
              label="Form"
              placeholder="Select form"
              options={formOptions}
              error={errors.form?.message}
              {...register('form')}
            />
          </div>

          <Input
            label="Price"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            error={errors.price?.message}
            {...register('price', { valueAsNumber: true })}
          />

          <Textarea
            label="Description"
            placeholder="Enter medication description..."
            rows={4}
            error={errors.description?.message}
            {...register('description')}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-100 dark:border-secondary-700">
            <Button type="button" variant="secondary" onClick={() => navigate('/medications')}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {isEdit ? 'Update Medication' : 'Add Medication'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default MedicationForm;



